import { Grid } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { GlutenFreeCodes } from '@pizza-hut-us-development/client-core';
import React, { ReactElement, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectors , updatePizzaCrust, updatePizzaSize } from '../../../slice/pizza.slice';
import { debouncedUpdateCrust, debouncedUpdateSize } from '../../../slice/debouncedActions';
import { selectCrustOptionForSize } from '../../../slice/selectors/crustSelectors';
import CrustsPicker from '../CrustsPicker';
import SizesPicker from '../SizesPicker';
import { crustCaloriesBySize } from '../../../hooks/pizzaIngredientOptionsSelectors';
import { extractGlobalId } from '../../../identifiers';
import useRestrictRuleModal from '../../../hooks/useRestrictRuleModal';
import { openModal } from '../../../../../localization/actions';
import BuilderSteps from '../../../BuilderSteps';
import { selectSizeOptionForCrust } from '../../../slice/selectors/sizeSelectors';
import { PizzaPayload } from '../../../slice/withCrustRules/withCrustRules';
import { CrustOption, PizzaIngredientOption } from '@/builders/pizza/dataTransformers/builderTypes';
import Routes from '@/router/routes';
import useDealStepDefaultHardcode from '../../../../../../hardcoding/utils/useDealStepDefaultVariantHardcode';
import { glutenFreeDetails } from '@/builders/pizza/PizzaBuilder/GlutenFreeDetails/GlutenFreeDetails';

const useStyles = makeStyles(() => ({
  container: {
    marginBottom: '-17px'
  }
}));

interface Props {
  toggleStep: (clickedStep: BuilderSteps) => void;
  currentPath?: string;
  isEditFlow?: boolean;
}

const SizeCrustSetterDesktop = ({ toggleStep, currentPath, isEditFlow }: Props): ReactElement => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const restrictRulesModal = useRestrictRuleModal();

  // pizza selectors
  const selectedSize = useSelector(selectors.selectPizzaSize);
  const selectedCrust = useSelector(selectors.selectPizzaCrust) || undefined;
  const sizes = useSelector(selectors.selectSizeOptions);
  const crusts = useSelector(selectors.selectCrustOptions) || undefined;
  const pizzaOptions = useSelector(selectors.selectPizzaOptions);
  const hasCollapsed = useRef(false);
  
  const handleGlutenFreeCrustModal = useCallback(({ crustID }: { crustID: string | undefined }) => {
    if(!crustID || !GlutenFreeCodes.has(crustID)) return;
  
    const getGlutenFreeModalContent = glutenFreeDetails(crustID);
    dispatch(openModal(getGlutenFreeModalContent));
}, [dispatch]);

  const getRestrictModalDetails = useCallback((
    size: PizzaIngredientOption | undefined | null,
    crust: CrustOption | null,
    updater: { payload: PizzaPayload | null; type: string }
  ): ModalContent | undefined => restrictRulesModal({
    sizeId: size?.id,
    crustId: crust?.id,
    ctaCallback: (builderType: BuilderSteps) => {
      dispatch(updater);
      toggleStep(builderType);
    }
  }), [dispatch, restrictRulesModal, toggleStep]);

  const selectSize = useCallback((size: PizzaIngredientOption | null) => {
    const crustToBeSelected = selectCrustOptionForSize(
      pizzaOptions,
      size,
      selectedCrust
    );
    // For modal, pass the action object, not the thunk
    const modalDetails = getRestrictModalDetails(
      size,
      crustToBeSelected,
      updatePizzaSize(size)
    );

    const genericCrusts = ['Hand Tossed Pizza', 'Original Pan® Pizza', 'Thin ‘N Crispy®'];

    if (!hasCollapsed.current && ((size && selectedCrust) !== undefined)
        && genericCrusts.includes(selectedCrust?.name as string)) {
      hasCollapsed.current = true;
      toggleStep(BuilderSteps.TOPPINGS);
    }

    return modalDetails
      ? dispatch(openModal(modalDetails))
      : dispatch(debouncedUpdateSize(size));
  }, [dispatch, getRestrictModalDetails, pizzaOptions, selectedCrust, toggleStep]);

  const selectCrust = useCallback((crust: CrustOption | null) => {
    handleGlutenFreeCrustModal({
      crustID: crust?.id
    });

    const sizeToBeSelected = selectSizeOptionForCrust(pizzaOptions, crust);
    const modalDetails = getRestrictModalDetails(
      sizeToBeSelected,
      crust,
      updatePizzaCrust(crust)
    );

    if (selectedSize !== null && crust !== null) toggleStep(BuilderSteps.TOPPINGS);
    if (currentPath === Routes.DEALS && crust !== null) toggleStep(BuilderSteps.TOPPINGS);

    if (modalDetails) {
      dispatch(openModal(modalDetails));
    } else if (crust) {
      dispatch(debouncedUpdateCrust(crust));
    }
  }, [currentPath, dispatch, getRestrictModalDetails, pizzaOptions, selectedSize, toggleStep, handleGlutenFreeCrustModal]);

  // rip line out once backend works through setting defaults correctly
  useDealStepDefaultHardcode({sizes, crusts: crusts || [], selectSize, selectCrust, skip: isEditFlow});

  return (
    <Grid container className={classes.container}>
      <SizesPicker
        sizes={sizes}
        selectedSize={selectedSize}
        onSelect={selectSize}
      />
      <CrustsPicker
        crusts={crusts || []}
        selectedCrust={selectedCrust || null}
        onSelect={selectCrust}
        crustCaloriesBySize={crustCaloriesBySize(extractGlobalId(selectedSize))}
      />
    </Grid>
  );
};

export default SizeCrustSetterDesktop;

